import React from 'react';
import PageWrapper from '../components/PageWrapper';
import { useAuthEffects } from '../components/Auth';
import { useGetCompanyProfileQuery } from '../services/companies';
import { useParams } from 'react-router-dom';
import { Avatar, Box, Container, Typography, useTheme, Link, useMediaQuery, ImageList, Button, Divider } from '@mui/material';
import { usePlansListByCompanyIdQuery } from '../services/plans';
import { PlanListItemEl } from '../components/PlanList';
import { ExpandableParagraph } from '../components/ExpandableParagraph';
import ytImage from '../assets/yt_logo_rgb_light.png';
import ytImageDark from '../assets/yt_logo_rgb_dark.png';
import igImage from '../assets/ig_glyph_black.svg';
import igImageDark from '../assets/ig_glyph_white.svg';
import pinterestImage from '../assets/pinterest_red.svg';
import pinterestImageDark from '../assets/pinterest_white.svg';
import tiktokImage from '../assets/tiktok.png';
import { Trans, useTranslation } from 'react-i18next';
import { getAuthorSubtitle } from '../common/util';
import { useWishlistLogic } from './discover/components/WishlistDialog';
import { useShippingRegionContext } from '../contexts/ShippingRegionContext';
import { useFavourites } from '../contexts/FavouritesContext';
import { Link as RouterLink } from 'react-router-dom';

const SellerProfileView = () => {
	useAuthEffects();
	const theme = useTheme();
	const {profileId} = useParams();
	const { selectedRegion } = useShippingRegionContext();
	const {data: companyProfile, isLoading: isCompanyProfileLoading} = useGetCompanyProfileQuery(profileId as string, {refetchOnMountOrArgChange: true, skip: !profileId});
	const {data: plans, isLoading: isPlansLoading} = usePlansListByCompanyIdQuery({companyId: companyProfile?.id as number, countryCode: selectedRegion as string}, { skip: !companyProfile || !selectedRegion });
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
	const isXsUp = useMediaQuery(theme.breakpoints.up('sm'));
	const { t, i18n } = useTranslation();
	const locale = i18n.language;
	const { favouriteIds } = useFavourites();
	const { handleAddFavouriteBtnClick, handleRemoveFavouriteBtnClick } = useWishlistLogic();

	const profileName = companyProfile?.vendorProfile?.name ?? companyProfile?.name;

	if (!companyProfile?.vendorProfile && !companyProfile?.roleCreator) {
		return <PageWrapper isLoading={isCompanyProfileLoading || isPlansLoading} noScroll>
			<Box flex={1} display='flex' flexDirection='column' justifyContent='center' alignItems='center' gap={2}>
				<Typography variant='h2'>Profile not found</Typography>
				<Button component={RouterLink} to='/'>Back to homepage</Button>
			</Box>
		</PageWrapper>;
	}

	return <PageWrapper isLoading={isCompanyProfileLoading || isPlansLoading} disableGutters maxWidth={'md'}>
		{companyProfile.coverImgUrl && <img src={companyProfile.coverImgUrl} alt="Cover" style={{width: '100%', objectFit: 'cover', maxHeight: '320px'}} />}
		<Container>
			<Box>
				<Box display='flex' flexDirection='row' gap={2} alignItems='center' alignSelf='flex-start' justifyContent='space-between'>
					<Box display='flex' flexDirection='row' gap={2} alignItems='center' alignSelf='flex-start' mt={2}>
						<Avatar sx={{width:64, height: 64, fontSize: 40, fontWeight: 400, mb: 2}} src={companyProfile?.imgUrl ?? ''}>{profileName?.charAt(0)}</Avatar>
						<div>
							<Typography variant='h1' sx={{fontSize: 24, textAlign: 'left', fontWeight: 600}}>{profileName}</Typography>
							<Typography variant='body1' fontSize={14} color='text.secondary'>{getAuthorSubtitle(companyProfile, t, locale)}</Typography>
						</div>
					</Box>
					
					<Box>
						<Box sx={{display: 'flex', gap: 3}}>
							{companyProfile?.ytChannelId && <Typography variant='body1' maxWidth={560} sx={{mt:2}}>
								<Link href={`https://www.youtube.com/channel/${companyProfile?.ytChannelId}`} target="_blank">
									<img src={theme.palette.mode === 'dark' ? ytImageDark : ytImage} alt="YouTube" style={{height: '16px', marginRight: '4px'}} />
								</Link>
							</Typography>}
							{companyProfile?.igChannelId && <Typography variant='body1' maxWidth={560} sx={{mt:2}}>
								<Link href={`https://www.instagram.com/${companyProfile?.igChannelId}`} target="_blank">
									<img src={theme.palette.mode === 'dark' ? igImageDark : igImage} alt="Instagram" style={{height: '24px'}} />
								</Link>
							</Typography>}
							{companyProfile?.pinterestChannelId && <Typography variant='body1' maxWidth={560} sx={{mt:2}}>
								<Link href={`https://www.pinterest.com/${companyProfile?.pinterestChannelId}`} target="_blank">
									<img src={theme.palette.mode === 'dark' ? pinterestImageDark : pinterestImage} alt="Pinterest" style={{height: '24px'}} />
								</Link>
							</Typography>}
							{companyProfile?.tiktokChannelId && <Typography variant='body1' maxWidth={560} sx={{mt:2}}>
								<Link href={`https://www.tiktok.com/${companyProfile?.tiktokChannelId}`} target="_blank">
									<img src={tiktokImage} alt="TikTok" style={{height: '24px'}} />
								</Link>
							</Typography>}
						</Box>	
					</Box>


				</Box>
				<ImageList gap={8} cols={isMdUp ? 4 : isXsUp ? 3 : 2} rowHeight={320} sx={{mb: 4}}>
					{(plans ?? []).map((item) => <PlanListItemEl
						key={item.id}
						item={item}
						subtitle={null}
						isFavourited={favouriteIds.has(item.id)}
						onAddFavouriteBtnClick={() => handleAddFavouriteBtnClick(item)}
						onRemoveFavouriteBtnClick={() => handleRemoveFavouriteBtnClick(item.id)}
					/>)}
				</ImageList>
				{companyProfile.bio && <Box>
					<Typography variant='h2' textAlign='left' sx={{fontSize: 24, fontWeight: 600, mt: 2, mb: 2, width: '100%'}}>{t('author.aboutSeller')}</Typography>
					<ExpandableParagraph text={companyProfile.bio} defaultLines={25} sx={{maxWidth: '720px', pb: 4}} />
				</Box>}

				{companyProfile.vendorProfile && 
				<Box>
					<Typography variant='h2' textAlign='left' sx={{fontSize: 24, fontWeight: 600, mt: 2, mb: 2, width: '100%'}}>{t('author.legalInformation')}</Typography>
					<Box gap={2} display='flex' flexDirection='column'>
						<div>
							<Typography variant='caption' textAlign='left'>{t('author.name')}</Typography>
							<Typography variant='body1' fontSize={14} color='text.secondary'>{companyProfile.vendorProfile.legalName}</Typography>
						</div>
						<div>
							<Typography variant='caption' textAlign='left'>{t('author.address')}</Typography>
							<Typography variant='body1' fontSize={14} color='text.secondary'>{companyProfile.vendorProfile.address}</Typography>
						</div>
						{companyProfile.vendorProfile.taxId && <div>
							<Typography variant='caption' textAlign='left'>{t('author.taxId')}</Typography>
							<Typography variant='body1' fontSize={14} color='text.secondary'>{companyProfile.vendorProfile.taxId}</Typography>
						</div>}
						{companyProfile.vendorProfile.website && <div>
							<Typography variant='caption' textAlign='left'>{t('author.website')}</Typography>
							<Typography variant='body1' fontSize={14} color='text.secondary'>{companyProfile.vendorProfile.website}</Typography>
						</div>}
					</Box>
				</Box>}

				{companyProfile.vendorProfile && <Box py={4}>
					<Divider />
					<Box display='flex' flexDirection='column' gap={2} alignItems='center' justifyContent='center' py={2}>
						<Typography variant='body1' fontSize={14} color='text.secondary'><Trans i18nKey='author.thisIsAVerifiedSeller' components={{ supportEmailLink: <Link href='mailto:support@diyfeed.com'>support@diyfeed.com</Link> }} /></Typography>
					</Box>
				</Box>}
			</Box>
		</Container>
	</PageWrapper>;
};

export default SellerProfileView;
